import React, { useCallback, useState } from 'react';
import AreYouSure from '../configs/dyson/AreYouSure';
import useWebSocketCaller from '../../hooks/useWebSocketCaller';
import { useOrganListenerContext } from '../../providers/OrganListenerContextProvider';
import PriorityBall from './PriorityBall';
import ProgressBar from './ProgressBar';
import { GrChapterPrevious } from "react-icons/gr";
import { GrChapterNext } from "react-icons/gr";
import { devStage, getNextStage, getPrevStage } from '../doit/ColorFolder';
import { durationAsLabel } from '../doit/PopingTask';
import './Taska.css';

const Taska = ({taskData}) => {
  const [visLevel, setVisLevel]         = useState('off'); // off, faded, on
  const [showConfirm, setShowConfirm]   = useState(false);
  const {sendMessage}                   = useWebSocketCaller();
  const {myTeamData, myAccountData}     = useOrganListenerContext();

  //------[  Mouse Leave  ]------
  //
  const handleMouseLeave = useCallback(() => {
    setVisLevel('off');
    setShowConfirm(false);
  }, []);

  //------[  Del Clicked  ]------
  //
  const onDelClicked = useCallback(() => {
    setShowConfirm(true);
  },[]);

  //------[  Top Prio  ]------
  //
  const onTopPrioClicked = useCallback(()=>{
    sendMessage('update-team-task', {
      teamId: myTeamData.teamId,
      accountId: myAccountData.accountId,
      taskId: taskData.taskId,
      fieldName: 'priority',
      fieldValue: '1' 
    });
    
  },[taskData.taskId, myTeamData.teamId, myAccountData.accountId, sendMessage]);


  //------[  Toggle Priority  ]------
  //
  const onTogglePriority = useCallback(()=>{
    // Toggle top/med/low for now...  should be red --> orange/purple --> gray
    const nextPriority = ( (taskData.priority+1) % 3) +1;
    sendMessage('update-team-task', {
      teamId: myTeamData.teamId,
      accountId: myAccountData.accountId,
      taskId: taskData.taskId,
      fieldName: 'priority',
      fieldValue: nextPriority
    });
  },[myAccountData.accountId, myTeamData.teamId, sendMessage, taskData.priority, taskData.taskId]);
  
  //------[  PREV Step  ]------
  //
  const stepPrev = useCallback(()=>{
    const prev = getPrevStage(taskData.stageId);
    sendMessage('update-team-task', {
      teamId: myTeamData.teamId,
      accountId: myAccountData.accountId,
      taskId: taskData.taskId,
      fieldName: 'stageId',
      fieldValue: prev.id
    });

    // TODO: Replace with SERVER UPDATE when updating the stageId.
    setTimeout(()=>{
      sendMessage('update-team-task', {
        teamId: myTeamData.teamId,
        accountId: myAccountData.accountId,
        taskId: taskData.taskId,
        fieldName: 'progress',
        fieldValue: prev.p
      });
    }, 750);
    
  },[taskData.taskId, myAccountData.accountId, myTeamData.teamId, sendMessage, taskData.stageId]);

  //------[  Step Next  ]------
  //
  const stepNext = useCallback(()=>{
    const next = getNextStage(taskData.stageId);
    sendMessage('update-team-task', {
      teamId: myTeamData.teamId,
      accountId: myAccountData.accountId,
      taskId: taskData.taskId,
      fieldName: 'stageId',
      fieldValue: next.id
    });

    sendMessage('update-team-task', {
      teamId: myTeamData.teamId,
      accountId: myAccountData.accountId,
      taskId: taskData.taskId,
      fieldName: 'progress',
      fieldValue: next.p
    });

  }, [myAccountData.accountId, myTeamData.teamId, sendMessage, taskData.stageId, taskData.taskId]);


  //------[  Delete Task  ]------
  //
  const deleteTask = useCallback(() => {
    sendMessage('delete-team-task',{
      taskId: taskData.taskId,
      teamId: myTeamData.teamId,
      accountId: myAccountData.accountId
    });
    setShowConfirm(false);
  }, [sendMessage, taskData.taskId, myTeamData.teamId, myAccountData.accountId]);

  
  //--- Render ---

  const stage = devStage.find(stage => taskData.stageId == stage.id);
  
  const taglets = taskData.tags?.map((tag, idx) => (
    <span key={idx} className='taskaTaglet'>
      {tag}
    </span>
  ));

  const estiLabel = durationAsLabel(taskData.estiUnit, taskData.estiCount);

  return (
    <div className='taskaWrap'>
      <div className={`taskaCtrls ${visLevel}`} onMouseEnter={()=>setVisLevel('on')} onMouseLeave={handleMouseLeave}>
        {showConfirm && <AreYouSure applyMe={deleteTask} actionTitle='Delete Task' hideMe={()=>setShowConfirm(false)}/>}
        {!showConfirm && <div className='taskaCtrlButtonsWrap'>
          <div className='taskaBtn' onClick={onDelClicked}>
            <span role="img" aria-label="delete">🗑️</span>
          </div>
          <div className='taskaBtn' onClick={onTopPrioClicked}>
            <span role="img" aria-label="delete">🡅</span> 
          </div>
        </div>}
      </div>
      <div className='taska' onMouseEnter={()=>setVisLevel('faded')} onMouseLeave={()=>setVisLevel('off')}>        
        <div className='taskaHdr'>
          <div className='taskaPrio'>
            {/* { taskData.progress == 100 && <span className='taskaDone'>✔️</span>} */}
            <PriorityBall priority={taskData.priority} handleAction={onTogglePriority}/>
          </div>

          <div className='taskaTitle'>
            {taskData.title}          
          </div>


          <div className='taskaAuxInfo'>
            <div className='taskaTags'>
              {taglets}
            </div>
            <div className='progWrap'>
              <ProgressBar progress={taskData.progress} title={stage?.t}/>
            </div>
            <div className='taskaEsti'>
              {estiLabel}
            </div>
          </div>

        </div>

        <div className='taskaBody'>
          <div className='taskaSpecial'>

          </div>
          <div className='taskaDesc'>
            {taskData.body}
          </div>

          <div className='taskaBodyAux'>
              <div className={`progCtrls ${visLevel}`}>
                <div className='progAuxBtn' onClick={stepPrev}>
                  <span role="img" aria-label="delete"><GrChapterPrevious /></span>
                </div>
                <div className='progAuxBtn' onClick={stepNext}>
                  <span role="img" aria-label="delete"><GrChapterNext /></span>
                </div>
              </div>

              <div className='estiCtrls'>

              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Taska;